import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import type { InsoleAdjustments } from "@/app/types/InsoleAdjustments";

export enum ZoneId {
  Base = "base",
  Sides = "sides",
  Top = "top",
}

export enum ZoneShape {
  Oval = "oval",
  Rectangle = "rectangle",
  Triangle = "triangle",
  Hexagon = "hexagon",
}

export enum ZoneKind {
  // Smooth additive relief (default).
  Bump = "bump",
  // Concave seat with a raised rim (e.g. heel cup).
  Cup = "cup",
  // Linear ramp at a measured angle (e.g. posting wedge).
  Wedge = "wedge",
}

export enum View2D {
  TOP = "top",
  BOTTOM = "bottom",
  LEFT = "left",
  RIGHT = "right",
  FRONT = "front",
  BACK = "back",
}

export enum ViewMode {
  MODE_3D = "3d",
  MODE_2D = "2d",
}

export enum FootSide {
  LEFT = "left",
  RIGHT = "right",
}

export enum ZoneSelection {
  All = "all",
  Base = "base",
  Sides = "sides",
  Top = "top",
}

export type SelectedZone = ZoneSelection | string;

export interface ActiveZone {
  id: string;
  parentZone: ZoneId.Top | ZoneId.Base;
  color: string;
  name: string;
  posX: number;
  posY: number;
  width: number;
  length: number;
  offsetX: number;
  offsetY: number;
  offsetZ: number;
  scaleX: number;
  scaleY: number;
  scaleZ: number;
  rotX: number;
  rotY: number;
  rotZ: number;
  shape: ZoneShape;
  kind?: ZoneKind;
  wedgeAngle?: number;
  hidden?: boolean;
}

export interface ZoneTransform {
  scaleX: number;
  scaleY: number;
  scaleZ: number;
  rotX: number;
  rotY: number;
  rotZ: number;
  posX: number;
  posY: number;
  posZ: number;
  // Top zone only: anchors the forefoot at this height (mm) so the talonnette
  // pivots from the front and the forefoot never drops below it.
  frontHeight: number;
  // Top zone only: heel height (mm); the top surface interpolates from the
  // forefoot (frontHeight) to the heel (backHeight).
  backHeight: number;
}

const defaultTransform = (): ZoneTransform => ({
  scaleX: 1,
  scaleY: 1,
  scaleZ: 1,
  rotX: 0,
  rotY: 0,
  rotZ: 0,
  posX: 0,
  posY: 0,
  posZ: 0,
  frontHeight: 0,
  backHeight: 0,
});

export interface GlobalScale {
  scaleX: number;
  scaleY: number;
  scaleZ: number;
}

export interface MeasurementRule {
  id: string;
  startIdx: number;
  endIdx: number;
  color: string;
  isVertical?: boolean;
  hidden?: boolean;
}

export interface Dimensions {
  width: number;
  length: number;
  height: number;
}

interface ViewerState {
  isWireframe: boolean;
  toggleWireframe: () => void;
  selectedZone: SelectedZone;
  setSelectedZone: (zone: SelectedZone) => void;
  hoveredZone: ZoneSelection | null;
  setHoveredZone: (zone: ZoneSelection | null) => void;
  hoveredActiveZoneId: string | null;
  setHoveredActiveZoneId: (id: string | null) => void;
  globalScale: GlobalScale;
  setGlobalScale: (s: Partial<GlobalScale>) => void;
  zoneTransforms: Record<ZoneId, ZoneTransform>;
  setZoneTransform: (zone: ZoneId, t: Partial<ZoneTransform>) => void;

  // Active Zones (Dynamic)
  activeZones: ActiveZone[];
  addActiveZone: (parent: ZoneId.Top | ZoneId.Base, color: string, name?: string) => string;
  removeActiveZone: (id: string) => void;
  updateActiveZone: (id: string, updates: Partial<ActiveZone>) => void;

  // Reset & Mirror
  resetTransforms: () => void;
  toggleMirror: () => void;

  // Persistable adjustments (per foot)
  getConfig: () => InsoleAdjustments;
  applyConfig: (config: InsoleAdjustments | null) => void;

  // Export
  requestExport: boolean;
  setRequestExport: (val: boolean) => void;
  exportFeet: FootSide[];
  setExportFeet: (feet: FootSide[]) => void;

  // Measurement
  isMeasuring: boolean;
  setIsMeasuring: (val: boolean) => void;
  // Single toggle: keep both measurements and global dimensions visible outside
  // measure mode.
  keepMeasurementsVisible: boolean;
  setKeepMeasurementsVisible: (val: boolean) => void;
  isVerticalMode: boolean;
  setIsVerticalMode: (val: boolean) => void;
  tempRule: number | null;
  setTempRule: (val: number | null) => void;
  rules: MeasurementRule[];
  addRule: (rule: MeasurementRule) => void;
  removeRule: (id: string) => void;
  toggleRuleHidden: (id: string) => void;
  setRuleColor: (id: string, color: string) => void;
  results: Record<string, number>;
  setResult: (id: string, val: number) => void;

  isGlobalXLong: boolean;
  setIsGlobalXLong: (val: boolean) => void;

  globalDimensions: Dimensions;
  setGlobalDimensions: (dim: Dimensions) => void;

  // Base Dimensions (for mm-based scaling minimums/maximums)
  baseDimensions: Record<ZoneSelection, Dimensions>;
  setBaseDimensions: (zone: ZoneSelection, dim: Dimensions) => void;

  // Current Physical Dimensions (for real-time UI display)
  currentZoneDimensions: Record<ZoneSelection, Dimensions>;
  setCurrentZoneDimensions: (zone: ZoneSelection, dim: Dimensions) => void;

  opacity: number;
  setOpacity: (val: number) => void;

  modelProgress: number;
  setModelProgress: (val: number) => void;

  isSceneReady: boolean;
  setIsSceneReady: (val: boolean) => void;

  viewMode: ViewMode;
  setViewMode: (val: ViewMode) => void;
  view2D: View2D;
  setView2D: (val: View2D) => void;

  requestRecenter: boolean;
  setRequestRecenter: (val: boolean) => void;

  footSide: FootSide;
  setFootSide: (side: FootSide) => void;

  // Manual override of the auto-detected handedness (mirror), for safety.
  invertSide: boolean;
  toggleInvertSide: () => void;

  zoomDelta: number;
  zoomIn: () => void;
  zoomOut: () => void;
}

const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);

const KEEP_VISIBLE_KEY = "configurator-keep-visible";

const readLocalBool = (key: string): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  return localStorage.getItem(key) === "true";
};

export const ViewerStore = create<ViewerState>()(
  subscribeWithSelector((set, get) => ({
    isWireframe: true,
    toggleWireframe: () => set((state) => ({ isWireframe: !state.isWireframe })),
    selectedZone: ZoneSelection.All,
    setSelectedZone: (zone) => set({ selectedZone: zone }),
    hoveredZone: null,
    setHoveredZone: (zone) => set({ hoveredZone: zone }),
    hoveredActiveZoneId: null,
    setHoveredActiveZoneId: (id) => set({ hoveredActiveZoneId: id }),
    globalScale: { scaleX: 1, scaleY: 1, scaleZ: 1 },
    setGlobalScale: (s: Partial<GlobalScale>) =>
      set((state) => {
        const next = { ...state.globalScale, ...s };
        const absX = Math.abs(next.scaleX);
        const signX = next.scaleX >= 0 ? 1 : -1;

        return {
          globalScale: {
            scaleX: signX * clamp(absX, 0.05, 5.0),
            scaleY: clamp(next.scaleY, 0.05, 5.0),
            scaleZ: clamp(next.scaleZ, 0.05, 5.0),
          },
        };
      }),
    zoneTransforms: {
      [ZoneId.Base]: defaultTransform(),
      [ZoneId.Sides]: defaultTransform(),
      [ZoneId.Top]: defaultTransform(),
    },
    setZoneTransform: (zone, t) =>
      set((state) => {
        const next = { ...state.zoneTransforms[zone], ...t };
        next.scaleX = clamp(next.scaleX, 0.05, 5.0);
        next.scaleY = clamp(next.scaleY, 0.05, 5.0);
        next.scaleZ = clamp(next.scaleZ, 0.05, 5.0);

        if (zone === ZoneId.Top) {
          next.posZ = clamp(next.posZ, 0.0, 100.0);
        }
        if (zone === ZoneId.Base) {
          next.posZ = clamp(next.posZ, -100.0, 0.0);
        }

        return {
          zoneTransforms: {
            ...state.zoneTransforms,
            [zone]: next,
          },
        };
      }),

    activeZones: [],
    addActiveZone: (parent, color, name) => {
      const id = `zone-${Math.random().toString(36).substr(2, 9)}`;
      set((state) => {
        const count = state.activeZones.filter((z) => z.parentZone === parent).length + 1;
        const isParentTop = parent === ZoneId.Top;
        const prefix = isParentTop ? "H" : "B";
        const finalName = name?.trim() ? name.trim() : `Z${prefix} ${count}`;

        const newZone: ActiveZone = {
          id,
          parentZone: parent,
          color,
          name: finalName,
          posX: 0,
          posY: 0,
          width: isParentTop ? 50 : 100,
          length: 50,
          offsetX: 0,
          offsetY: 0,
          offsetZ: 0,
          scaleX: 1,
          scaleY: 1,
          scaleZ: 1,
          rotX: 0,
          rotY: 0,
          rotZ: 0,
          shape: parent === ZoneId.Base ? ZoneShape.Rectangle : ZoneShape.Oval,
          kind: ZoneKind.Bump,
          wedgeAngle: 0,
        };
        return {
          activeZones: [...state.activeZones, newZone],
          selectedZone: id,
        };
      });
      return id;
    },
    removeActiveZone: (id) =>
      set((state) => ({
        activeZones: state.activeZones.filter((z) => z.id !== id),
        selectedZone: state.selectedZone === id ? ZoneSelection.All : state.selectedZone,
      })),
    updateActiveZone: (id, updates) =>
      set((state) => ({
        activeZones: state.activeZones.map((z) => (z.id === id ? { ...z, ...updates } : z)),
      })),

    resetTransforms: () =>
      set({
        globalScale: { scaleX: 1, scaleY: 1, scaleZ: 1 },
        zoneTransforms: {
          [ZoneId.Base]: defaultTransform(),
          [ZoneId.Sides]: defaultTransform(),
          [ZoneId.Top]: defaultTransform(),
        },
        activeZones: [],
        selectedZone: ZoneSelection.All,
        rules: [],
        results: {},
      }),
    toggleMirror: () =>
      set((state) => ({
        globalScale: { ...state.globalScale, scaleX: state.globalScale.scaleX * -1 },
        activeZones: state.activeZones.map((z) => ({
          ...z,
          posX: -z.posX,
          rotZ: -z.rotZ,
        })),
      })),

    getConfig: () => {
      const state = get();
      return {
        globalScale: state.globalScale,
        zoneTransforms: state.zoneTransforms,
        activeZones: state.activeZones,
        rules: state.rules,
        results: state.results,
        invertSide: state.invertSide,
      };
    },
    applyConfig: (config) =>
      set({
        globalScale: config ? config.globalScale : { scaleX: 1, scaleY: 1, scaleZ: 1 },
        zoneTransforms: config
          ? {
              // Merge with defaults so configs saved before new fields (e.g.
              // frontHeight) load without leaving undefined values.
              [ZoneId.Base]: { ...defaultTransform(), ...config.zoneTransforms[ZoneId.Base] },
              [ZoneId.Sides]: { ...defaultTransform(), ...config.zoneTransforms[ZoneId.Sides] },
              [ZoneId.Top]: { ...defaultTransform(), ...config.zoneTransforms[ZoneId.Top] },
            }
          : {
              [ZoneId.Base]: defaultTransform(),
              [ZoneId.Sides]: defaultTransform(),
              [ZoneId.Top]: defaultTransform(),
            },
        activeZones: config ? config.activeZones : [],
        rules: config ? config.rules : [],
        results: config ? config.results : {},
        invertSide: config ? (config.invertSide ?? false) : false,
        selectedZone: ZoneSelection.All,
        hoveredZone: null,
      }),

    requestExport: false,
    setRequestExport: (val) => set({ requestExport: val }),
    exportFeet: [],
    setExportFeet: (feet) => set({ exportFeet: feet }),

    // Measurement
    isMeasuring: false,
    setIsMeasuring: (val) => set({ isMeasuring: val }),
    keepMeasurementsVisible: readLocalBool(KEEP_VISIBLE_KEY),
    setKeepMeasurementsVisible: (val) => {
      if (typeof window !== "undefined") {
        localStorage.setItem(KEEP_VISIBLE_KEY, String(val));
      }
      set({ keepMeasurementsVisible: val });
    },
    isVerticalMode: true,
    setIsVerticalMode: (val) => set({ isVerticalMode: val }),
    tempRule: null,
    setTempRule: (val) => set({ tempRule: val }),
    rules: [],
    addRule: (rule) => set((state) => ({ rules: [...state.rules, rule] })),
    removeRule: (id) =>
      set((state) => {
        const newResults = { ...state.results };
        delete newResults[id];
        return {
          rules: state.rules.filter((r) => r.id !== id),
          results: newResults,
        };
      }),
    toggleRuleHidden: (id) =>
      set((state) => ({
        rules: state.rules.map((r) => (r.id === id ? { ...r, hidden: !r.hidden } : r)),
      })),
    setRuleColor: (id, color) =>
      set((state) => ({
        rules: state.rules.map((r) => (r.id === id ? { ...r, color } : r)),
      })),
    results: {},
    setResult: (id: string, val: number) =>
      set((state) => ({ results: { ...state.results, [id]: val } })),

    isGlobalXLong: false,
    setIsGlobalXLong: (val) => set({ isGlobalXLong: val }),

    globalDimensions: { width: 0, length: 0, height: 0 },
    setGlobalDimensions: (dim) => set({ globalDimensions: dim }),

    baseDimensions: {
      [ZoneSelection.All]: { width: 0, length: 0, height: 0 },
      [ZoneSelection.Base]: { width: 0, length: 0, height: 0 },
      [ZoneSelection.Top]: { width: 0, length: 0, height: 0 },
      [ZoneSelection.Sides]: { width: 0, length: 0, height: 0 },
    },
    setBaseDimensions: (zone, dim) =>
      set((state) => ({
        baseDimensions: { ...state.baseDimensions, [zone]: dim },
      })),

    currentZoneDimensions: {
      [ZoneSelection.All]: { width: 0, length: 0, height: 0 },
      [ZoneSelection.Base]: { width: 0, length: 0, height: 0 },
      [ZoneSelection.Top]: { width: 0, length: 0, height: 0 },
      [ZoneSelection.Sides]: { width: 0, length: 0, height: 0 },
    },
    setCurrentZoneDimensions: (zone, dim) =>
      set((state) => ({
        currentZoneDimensions: { ...state.currentZoneDimensions, [zone]: dim },
      })),

    opacity: 1.0,
    setOpacity: (val: number) => set({ opacity: val }),

    modelProgress: 0,
    setModelProgress: (val) => set({ modelProgress: val }),

    isSceneReady: false,
    setIsSceneReady: (val) => set({ isSceneReady: val }),

    viewMode: ViewMode.MODE_3D,
    setViewMode: (val) => set({ viewMode: val }),
    view2D: View2D.TOP,
    setView2D: (val) => set({ view2D: val }),

    requestRecenter: false,
    setRequestRecenter: (val) => set({ requestRecenter: val }),

    footSide: FootSide.LEFT,
    setFootSide: (side) => set({ footSide: side }),

    invertSide: false,
    toggleInvertSide: () => set((state) => ({ invertSide: !state.invertSide })),

    zoomDelta: 0,
    zoomIn: () => set((state) => ({ zoomDelta: state.zoomDelta + 1 })),
    zoomOut: () => set((state) => ({ zoomDelta: state.zoomDelta - 1 })),
  })),
);
